import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Check, ArrowRight, ExternalLink, Truck, Headset, GraduationCap } from "lucide-react";

const IMG = {
  dispatcher: "https://images.unsplash.com/photo-1581089781785-603411fa81e5?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
};

const Highlight = ({ children }) => (
  <li className="flex items-start gap-2 text-sm text-dark-text/80">
    <Check size={16} className="text-magenta mt-0.5 shrink-0" /> {children}
  </li>
);

export default function Programs() {
  return (
    <div className="App">
      <Header />
      <section className="bg-navy text-white py-16" data-testid="programs-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-xs font-bold uppercase tracking-widest text-magenta">Programs</span>
          <h1 className="mt-3 font-display text-4xl lg:text-5xl font-black">One Intake. Multiple Career Pathways.</h1>
          <p className="mt-4 text-white/80 max-w-2xl">Choose the direction that fits your goals. The intake only shows the questions connected to the programs you select.</p>
        </div>
      </section>

      <section className="bg-white py-20" data-testid="programs-list">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">

          {/* Dispatcher */}
          <div id="dispatcher" className="grid lg:grid-cols-2 gap-10 items-center">
            <div className="rounded-lg overflow-hidden border border-navy/10 shadow-sm order-1">
              <img src={IMG.dispatcher} alt="Logistics dispatcher at a desk" className="w-full h-72 object-cover" />
            </div>
            <div className="order-2">
              <span className="inline-flex items-center gap-2 text-magenta font-bold text-sm"><Headset size={18} /> Certified Dispatcher</span>
              <h2 className="mt-2 font-display text-2xl lg:text-3xl font-bold text-navy">AAWTA Certified Dispatcher Program</h2>
              <p className="mt-3 text-dark-text/80 leading-relaxed">A hands-on, interactive program taught by a certified trainer. Training may be completed virtually or in person, with financing options available. Participants may complete the course in fewer than four weeks and continue with structured mentorship after graduation.</p>
              <ul className="mt-5 grid sm:grid-cols-2 gap-2">
                <Highlight>Hands-on and interactive</Highlight>
                <Highlight>Certified trainer taught</Highlight>
                <Highlight>Virtual or in-person options</Highlight>
                <Highlight>Complete in fewer than four weeks</Highlight>
                <Highlight>Financing options available</Highlight>
                <Highlight>Mentorship included</Highlight>
                <Highlight>Minimum applicant age: 16</Highlight>
              </ul>
              <Link to="/intake" data-testid="program-dispatcher-cta" className="mt-6 inline-flex items-center gap-2 bg-magenta hover:bg-magenta-dark text-white font-bold py-3 px-6 rounded-md transition-colors">
                Explore Dispatcher Training <ArrowRight size={16} />
              </Link>
            </div>
          </div>

          {/* CDL */}
          <div id="cdl" className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="inline-flex items-center gap-2 text-magenta font-bold text-sm"><Truck size={18} /> Class A CDL</span>
              <h2 className="mt-2 font-display text-2xl lg:text-3xl font-bold text-navy">Behind the Wheel: Class A CDL Pathway</h2>
              <p className="mt-3 text-dark-text/80 leading-relaxed">Qualified applicants may train directly with AAWTA's education partner, 160 Driving Academy. This pathway combines professional CDL instruction with workforce-development connections, financing or sponsored-tuition options, AAWTA mentorship, and additional training opportunities following graduation.</p>
              <ul className="mt-5 grid sm:grid-cols-2 gap-2">
                <Highlight>Direct partner referral</Highlight>
                <Highlight>Hands-on Class A CDL training</Highlight>
                <Highlight>Workforce pathway connections</Highlight>
                <Highlight>Financing and sponsored-tuition options</Highlight>
                <Highlight>AAWTA mentorship</Highlight>
                <Highlight>Career and post-graduation support</Highlight>
                <Highlight>Partner qualification review required</Highlight>
              </ul>
              <div className="mt-4 bg-navy-light border border-navy/10 rounded-md p-4 text-sm text-navy">
                The questionnaire provides preliminary readiness information. <strong>160 Driving Academy makes final admission and funding decisions.</strong>
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/intake" data-testid="program-cdl-cta" className="inline-flex items-center gap-2 bg-magenta hover:bg-magenta-dark text-white font-bold py-3 px-6 rounded-md transition-colors">
                  Explore the CDL Pathway <ArrowRight size={16} />
                </Link>
                <a href="https://www.160drivingacademy.com/?utm_source=Internal_Marketing&utm_medium=AAWTA" target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-2 border-2 border-navy text-navy hover:bg-navy-light font-bold py-3 px-6 rounded-md transition-colors">
                  160 Driving Academy <ExternalLink size={16} />
                </a>
              </div>
            </div>
            <div className="rounded-lg overflow-hidden border border-navy/10 shadow-sm bg-black flex items-center justify-center p-10 h-72">
              <img src="/partner-160.png" alt="160 Driving Academy — Truck Driver Training" className="max-h-full max-w-[240px] object-contain" data-testid="partner-logo-160" />
            </div>
          </div>

          {/* WGU */}
          <div id="wgu" className="grid lg:grid-cols-2 gap-10 items-center">
            <div className="rounded-lg overflow-hidden border border-navy/10 shadow-sm bg-white flex items-center justify-center p-8 h-72 order-1">
              <img src="/partner-wgu.png" alt="Western Governors University (WGU)" className="max-h-32 max-w-[360px] w-full object-contain" data-testid="partner-logo-wgu" />
            </div>
            <div className="order-2">
              <span className="inline-flex items-center gap-2 text-magenta font-bold text-sm"><GraduationCap size={18} /> Behind the Desk</span>
              <h2 className="mt-2 font-display text-2xl lg:text-3xl font-bold text-navy">Behind the Desk: WGU Certificate Pathways</h2>
              <p className="mt-3 text-dark-text/80 leading-relaxed">Build job-ready business, leadership, project-management, entrepreneurship, and supply-chain skills through flexible university-backed certificate programs and AAWTA pathway support.</p>
              <div className="mt-5 grid sm:grid-cols-2 gap-3">
                {[
                  ["Supply Chain Fundamentals Certificate", "https://www.wgu.edu/online-business-degrees/certificates/supply-chain.html"],
                  ["Business Leadership Certificate", "https://www.wgu.edu/online-business-degrees/certificates/leadership.html"],
                  ["Entrepreneurship Fundamentals Certificate", "https://www.wgu.edu/online-business-degrees/certificates/entrepreneurship-fundamentals.html"],
                  ["Project Management Certificate", "https://www.wgu.edu/online-business-degrees/certificates/project-management-certificate.html"],
                ].map(([label, url]) => (
                  <a key={label} href={url} target="_blank" rel="noopener noreferrer"
                     className="flex items-center justify-between gap-2 bg-white border border-navy/10 hover:border-magenta rounded-md p-3 text-sm font-semibold text-navy transition-colors">
                    {label} <ExternalLink size={14} className="text-magenta shrink-0" />
                  </a>
                ))}
              </div>
              <Link to="/intake" data-testid="program-wgu-cta" className="mt-6 inline-flex items-center gap-2 bg-magenta hover:bg-magenta-dark text-white font-bold py-3 px-6 rounded-md transition-colors">
                Explore WGU Certificates <ArrowRight size={16} />
              </Link>
            </div>
          </div>

        </div>
      </section>
      <Footer />
    </div>
  );
}
